import { Text, Image, useBreakpointProp } from '@betty/moonlight';
import styles from './ResponsibleGaming.module.css';
import { TypographyNames } from '@betty/moonlight/lib/common/Text/Text.types';
import { Page } from '@betty/navigation';

function ResponsibleGambling() {
  const textVariant = useBreakpointProp<TypographyNames>({
    mobile: 'body-m',
    tablet: 'body-m',
    desktop: 'body-l',
  });

  const headerVariant = useBreakpointProp<TypographyNames>({
    mobile: 'h3',
    tablet: 'h3',
    desktop: 'h2',
  });

  const mainHeadingVariant = useBreakpointProp<TypographyNames>({
    mobile: 'h4',
    tablet: 'h4',
    desktop: 'h3',
  });

  const secondaryHeadingVariant = useBreakpointProp<TypographyNames>({
    mobile: 'h6',
    tablet: 'h6',
    desktop: 'h5',
  });

  return (
    <Page
      titleKey="Betty.ca - Responsible Gaming Policy"
      descriptionKey='Betty Gaming is fully committed to Responsible Gaming. We strive to ensure that our Players are gambling within their means and in a responsible and controlled manner.'
    >
      <main className={styles.rgMain}>
      <div className={styles.wrapper}>
        <div className={styles.heroSectionWrap}>
          <Image
            name="misc.rgHeader"
            height="100%"
            width="100%"
            contentFit="cover"
          />
          <Image
            name="misc.rgCheck"
            position="absolute"
            top="space-0"
            right="space-0"
            width={72}
            height={72}
          />
        </div>

        <article className={styles.article}>
          <Text variant={headerVariant} gradient="darkSurfaceAccent">
            Responsible Gaming
          </Text>
          <Text variant="body-l-semi" color="darkSurfacePrimary">
            At Betty, your enjoyment is our number one priority.
          </Text>

          <div className={`${styles.rowArticle} ${styles.reorder}`}>
            <div className={styles.child}>
              <Text variant={textVariant} color="darkSurfacePrimary">
                We believe gaming is fun and rewarding, and it should not become
                a problem to you or your family, nor should it get in the way of
                your daily responsibilities. However, we acknowledge gambling
                addictions can be a real challenge and we are here to help you
                game responsibly.
              </Text>
            </div>

            <div>
              <Text variant={textVariant} color="darkSurfacePrimary">
                Please, Play Responsibly. If you, or someone you know, has a
                gambling problem and wants help, please visit{' '}
                <a
                  href="https://connexontario.ca/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ConnexOntario
                </a>{' '}
                or call their help line at{' '}
                <a href="tel:1-866-531-2600">1-866-531-2600</a>.
              </Text>
            </div>

            <div className={styles.child}>
              <Image
                name="misc.rgArticle1"
                width="100%"
                contentFit="contain"
                aspectRatio="16/9"
              />
            </div>
          </div>
        </article>

        <article className={styles.article}>
          <Text variant={mainHeadingVariant} gradient="darkSurfaceAccent">
            What is Problem Gambling
          </Text>

          <Text variant={textVariant} color="darkSurfacePrimary">
            A problem gambler is someone unable to control or stop their
            gambling despite adverse consequences. Often, problem gamblers are
            unable to keep their financial obligations and responsibilities.
            Other areas of the problem gambler&apos;s life are also affected,
            such as work and school performance, mental health, and family and
            social relationships. Problem gamblers typically gamble more than
            they can afford, and often max out credit cards and borrow money to
            fund their gambling problem.
          </Text>

          <div className={styles.subGuidelinesWrap}>
            <Text
              variant={secondaryHeadingVariant}
              gradient="darkSurfaceAccent"
            >
              Common warning signs of problem gambling include:
            </Text>

            <ul>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Feeling guilty, anxious, frustrated, or worried about your
                  gambling
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Thinking or talking about gambling more than usual
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Gambling to win back money you’ve lost or thinking that a “big
                  win” is right around the corner
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Experiencing extreme highs from wins and extreme lows from
                  losses
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Finding it difficult to control or stop gambling, or feeling
                  irritable when you try to stop
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Feeling a sense of emptiness or loss when you’re not gambling
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Borrowing money, selling things, committing (or considering
                  committing) criminal acts to obtain money for gambling
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Having increased debt, unpaid bills, or other financial
                  troubles because of your gambling
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Gambling to escape personal problems or to relieve feelings of
                  anxiety, depression, anger, or loneliness
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Gambling instead of attending family or other social functions
                  or neglecting family or household responsibilities because of
                  gambling
                </Text>
              </li>
            </ul>
          </div>

          <div className={styles.subGuidelinesWrap}>
            <Text
              variant={secondaryHeadingVariant}
              gradient="darkSurfaceAccent"
            >
              Financial warning signs of problem gaming include:
            </Text>

            <ul>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Overdue and unpaid bills
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Increased spending on credit cards/significant debt increase
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Secretive about money problems and/or account balance(s) to
                  friends and family
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Frequently short/lacking money despite sufficient income
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Repeatedly borrowing money from friends and/or family
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Money disappearing from savings account, home, or wallets
                  without explanation
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Receiving calls from collection agencies
                </Text>
              </li>
            </ul>
          </div>
        </article>

        <article className={styles.article}>
          <Text variant={mainHeadingVariant} gradient="darkSurfaceAccent">
            Self Assessment
          </Text>

          <div className={styles.subGuidelinesWrap}>
            <Text variant={textVariant} color="darkSurfacePrimary">
              Take a self assessment with{' '}
              <a
                href="https://gamcare.gamtest.se/"
                target="_blank"
                rel="noopener noreferrer"
              >
                gamcare.gamtest.se
              </a>{' '}
              or ask yourself the following questions:
            </Text>

            <ul>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Is gambling interfering with your work or other
                  responsibilities?
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Are you gambling more than you can afford to lose?
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Have you borrowed money or sold anything to gamble?
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Are you trying to make up for previous gambling losses?
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Have you needed to gamble with larger amounts of money to get
                  the same level of excitement?
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Are you recovering from an addictive disorder?
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Do you gamble under the influence of alcohol or other
                  substances?
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Have you ever gambled to escape worry, trouble or boredom?
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Has gambling caused you any health problems, including stress
                  or anxiety?
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Has your gambling caused any financial problems for you or
                  your family?
                </Text>
              </li>
            </ul>

            <Text variant={textVariant} color="darkSurfacePrimary">
              If you answered “yes” to one or more questions, then you might
              want to consider looking at your gambling more closely.
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              You may be struggling with issues related to gambling. Reaching
              out for help is a big step and we encourage you to seek support as
              soon as possible.
            </Text>
          </div>
        </article>

        <article className={styles.article}>
          <Text variant={mainHeadingVariant} gradient="darkSurfaceAccent">
            Problem Gambling Support & Help
          </Text>

          <div className={styles.rowArticle}>
            <div className={styles.child}>
              <Text variant={textVariant} color="darkSurfacePrimary">
                We at Betty Gaming want to ensure that players who may be
                experiencing gambling related harm are provided resources for
                assistance. It is of our utmost value that such players receive
                support and assistance from reputable sources with expertise in
                problem gambling. Such resources include:
              </Text>
            </div>

            <div className={styles.child}>
              <Image
                contentFit="contain"
                name="misc.rgArticle2"
                width="100%"
                aspectRatio="16/9"
              />
            </div>
          </div>
          <Text variant={secondaryHeadingVariant} gradient="darkSurfaceAccent">
            ConnexOntario
          </Text>

          <div className={styles.subGuidelinesWrap}>
            <Text variant={textVariant} gradient="darkSurfaceAccent">
              <a
                href="https://www.connexontario.ca/en-ca/"
                target="_blank"
                rel="noopener noreferrer"
              >
                (https://www.connexontario.ca/en-ca/)
              </a>
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              ConnexOntario connects you to mental health, addiction, and
              problem gambling services. They are available 24/7 and provide
              free and confidential services. They can be reached at{' '}
              <a href="tel:1-866-531-2600" className={styles.lightLinks}>
                1-866-531-2600
              </a>{' '}
              for phone support, 247247 for text support, and chat and email
              support through their website. ConnexOntario offers:
            </Text>

            <ul>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  contact information for mental health and addiction services
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  basic education about gambling, drugs, alcohol, or mental
                  health problems
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  listening, offering support, and providing strategies to help
                  you meet your goals
                </Text>
              </li>
            </ul>
          </div>

          <Text variant={secondaryHeadingVariant} gradient="darkSurfaceAccent">
            Responsible Gambling Council
          </Text>

          <div className={styles.subGuidelinesWrap}>
            <Text variant={textVariant} gradient="darkSurfaceAccent">
              <a
                href="https://www.responsiblegambling.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                (https://www.responsiblegambling.org/)
              </a>
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              The RGC is a respected, independent non-profit organization that
              has been a leader in preventing problem gambling in Canada for
              more than 35 years. Their website offers a variety of support
              services and options that help get you care you need. Many of the
              options and services detailed within their website are free of
              charge.
            </Text>
          </div>

          <Text variant={secondaryHeadingVariant} gradient="darkSurfaceAccent">
            The Centre for Addiction and Mental Health
          </Text>

          <div className={styles.subGuidelinesWrap}>
            <Text variant={textVariant} gradient="darkSurfaceAccent">
              <a
                href="https://www.camh.ca/"
                target="_blank"
                rel="noopener noreferrer"
              >
                (https://www.camh.ca/)
              </a>
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              CAMH is Canada&apos;s largest mental health teaching hospital and
              one of the world&apos;s leading research centres in its field.
              CAMH is fully affiliated with the University of Toronto and is a
              Pan American Health Organization/World Health Organization
              Collaborating Centre. CAMH can be contacted by phone at{' '}
              <a href="tel:416 535-8501" className={styles.lightLinks}>
                416 535-8501
              </a>{' '}
              or{' '}
              <a href="tel:1 800 463-2338" className={styles.lightLinks}>
                1 800 463-2338
              </a>{' '}
              (toll-free). Gambling, Gaming and Technology Use, part of the
              Provincial Support Program at CAMH, can also be contacted via
              email at{' '}
              <a href="mailto:webmaster@problemgambling.ca">
                webmaster@problemgambling.ca
              </a>
              .
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              For additional resources and responsible gaming information,
              please visit{' '}
              <a
                href="https://igamingontario.ca/en/player/responsible-gambling"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://igamingontario.ca/en/player/responsible-gambling
              </a>
            </Text>
          </div>
        </article>

        <article className={styles.article}>
          <Text variant={mainHeadingVariant} gradient="darkSurfaceAccent">
            Partners
          </Text>

          <Text variant={textVariant} color="darkSurfacePrimary">
            Betty is proud to be partnering with Birches Health, a leading
            provider of Responsible Gaming resources and specialized Problem
            Gaming Treatment. Together with Birches Health we have developed
            custom programs, exercises and content to equip players with skills
            and tools that decrease the likelihood of problem gaming issues. You
            can engage with our educational resources in several ways:
          </Text>

          <div className={styles.rowArticle}>
            <div className={styles.child}>
              <div className={styles.subGuidelinesWrap}>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Self-guided exercises to stay engaged between sessions
                </Text>

                <ul>
                  <li>
                    <Text variant={textVariant} color="darkSurfacePrimary">
                      Content library with engaging videos on multiple topics
                      related to responsible or problem gaming
                    </Text>
                  </li>
                  <li>
                    <Text variant={textVariant} color="darkSurfacePrimary">
                      Articles and resources related to thematic topics
                      highlighting at-risk behavior
                    </Text>
                  </li>
                  <li>
                    <Text variant={textVariant} color="darkSurfacePrimary">
                      Special calculator revealing potential time and money
                      spent on gambling
                    </Text>
                  </li>
                </ul>
              </div>
            </div>

            <div className={styles.child}>
              <Image
                name="misc.rgArticle3"
                width="100%"
                contentFit="contain"
                aspectRatio="16/9"
              />
            </div>
          </div>

          <Text variant={textVariant} color="darkSurfacePrimary">
            More information on the program presented by Birches Health and
            Betty can be located on{' '}
            <a
              href="https://bircheshealth.com/betty"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://bircheshealth.com/betty
            </a>
          </Text>
        </article>

        <article className={styles.article}>
          <Text variant={mainHeadingVariant} gradient="darkSurfaceAccent">
            Protecting underage users
          </Text>

          <div className={styles.subGuidelinesWrap}>
            <Text variant={textVariant} color="darkSurfacePrimary">
              It is not permitted for anyone under the age of 19 to create an
              account or play at our casino.
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              We ask that you take reasonable steps to prevent anyone under the
              age of 19 from accessing and playing in the casino:
            </Text>

            <ul>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Do not leave under 19s unattended near your computer or mobile
                  when signed in to the casino
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Do not allow persons under 19 to participate in any gambling
                  activity
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Do not share credit card or bank account details with under
                  19s
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Do not use the &quot;Save Password&quot; option when logging
                  into the casino
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Create separate profiles for everyone who uses your computer
                </Text>
              </li>
              <li>
                <Text variant={textVariant} color="darkSurfacePrimary">
                  Use child-protection software, such as{' '}
                  <a
                    href="https://www.netnanny.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Net Nanny
                  </a>{' '}
                  to block gambling sites from minors
                </Text>
              </li>
            </ul>

            <Text variant={textVariant} color="darkSurfacePrimary">
              If you know someone under the age of 19 who is registered with us
              or accessing the casino, please contact us immediately at{' '}
              <a href="mailto:support@betty.ca">support@betty.ca</a>
            </Text>
          </div>
        </article>

        <article className={styles.article}>
          <Text variant={mainHeadingVariant} gradient="darkSurfaceAccent">
            Individual Protection
          </Text>

          <div className={styles.rowArticle}>
            <div className={styles.child}>
              <Text variant={textVariant} color="darkSurfacePrimary">
                Betty Gaming expects players to only gamble within their means.
                We understand that sometimes a Player would wish to set such
                limitations in an effort to control their gambling. As part of
                our commitment to Responsible Gaming, we offer various tools.
              </Text>
            </div>
            <div className={styles.child}>
              <Image
                name="misc.rgArticle4"
                contentFit="contain"
                width="100%"
                aspectRatio="16/9"
              />
            </div>
          </div>

          <Text variant={secondaryHeadingVariant} gradient="darkSurfaceAccent">
            Betty Player Protection Tools
          </Text>

          <div className={styles.subGuidelinesWrap}>
            <ul className={styles.goldBullets}>
              <li>
                <Text variant={textVariant} gradient="darkSurfaceAccent">
                  Deposit Limits
                </Text>
              </li>
            </ul>

            <Text variant={textVariant} color="darkSurfacePrimary">
              You can set a limit to the total amount you can deposit over a
              certain period. Once that limit is reached, you won’t be able to
              make any more deposits until the period is over. The deposit limit
              facility allows you to set a limit either daily (00:00 to 23:59),
              weekly (Sunday to Saturday) or monthly (calendar month). Daily,
              weekly and monthly limits are independent, and you can have
              multiple limits active at any time.
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              To set a deposit limit, visit Account &gt; Responsible Gaming &gt;
              Deposit Limits and enter the amount you wish to set. If you are
              updating a limit to make it more restrictive, the updated value
              will go into effect immediately. However, if you’re looking to
              increase or remove your limit, the update will be enforced only
              after the mandatory 24h cooling off period.
            </Text>

            <ul className={styles.goldBullets}>
              <li>
                <Text variant={textVariant} gradient="darkSurfaceAccent">
                  Loss Limits
                </Text>
              </li>
            </ul>

            <Text variant={textVariant} color="darkSurfacePrimary">
              You can set a limit to the amount you can lose. The loss limit
              facility allows you to set a limit either daily (00:00 to 23:59),
              weekly (Sunday to Saturday) or monthly (calendar month). Daily,
              weekly and monthly limits are independent, and you can have
              multiple limits active at any time. Please note that the loss
              limit is a net limit, meaning any money won during a given limit
              period will be added to your limit amount for that period.
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              To set a loss limit, visit Account &gt; Responsible Gaming &gt;
              Loss Limit and enter the amount you wish to set. If you are
              updating a limit to make it more restrictive, the updated value
              will go into effect immediately. However, if you’re looking to
              increase or remove your limit, the update will be enforced only
              after the mandatory 24h cooling off period.
            </Text>

            <ul className={styles.goldBullets}>
              <li>
                <Text variant={textVariant} gradient="darkSurfaceAccent">
                  Playtime Limits
                </Text>
              </li>
            </ul>

            <Text variant={textVariant} color="darkSurfacePrimary">
              You can limit the time you can spend in real money play over a
              certain period. Once that limit is reached, you won’t be able to
              place any more bets during real money play until the period is
              over. The playtime limit facility allows you to set a limit either
              daily (00:00 to 23:59), weekly (Sunday to Saturday), or monthly
              (calendar month). Daily, weekly, and monthly limits are
              independent, and you can have multiple limits active at any time.
              To set a playtime limit, visit Account &gt; Responsible Gaming
              &gt; Playtime Limits and enter the time in hours you wish to set.
              If you update the limit to make it more restrictive, the updated
              value will go into effect immediately. However, if you want to
              increase or remove your limit, the update will be enforced only
              after the mandatory 24-hour cooling-off period.
            </Text>

            <ul className={styles.goldBullets}>
              <li>
                <Text variant={textVariant} gradient="darkSurfaceAccent">
                  Timeout
                </Text>
              </li>
            </ul>

            <Text variant={textVariant} color="darkSurfacePrimary">
              We understand that sometimes our Players would like to take a
              break from gambling. We offer varying break periods that prohibit
              you from gambling at Betty Gaming for a certain period of time.
              You may request a one day, two days, three days, four days, five
              days, six days, one week, two weeks, three weeks, one month, two
              months, or three months break at any time.
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              To do so, visit Account &gt; Responsible Gaming &gt; Timeout and
              select the duration you wish to set.
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              Once you begin a Timeout, you will not be able to use your account
              for playing, however, you will still be able to log in to withdraw
              any remaining balance. It will not be possible to reactivate your
              account until your selected period has ended.
            </Text>

            <ul className={styles.goldBullets}>
              <li>
                <Text variant={textVariant} gradient="darkSurfaceAccent">
                  Self-Exclusion
                </Text>
              </li>
            </ul>

            <Text variant={textVariant} color="darkSurfacePrimary">
              If at any time you become concerned about your gambling, you may
              request to be self-excluded from Betty Gaming for terms lasting
              six months, nine months, one year, two years, four years or five
              years.
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              To do so, visit Account &gt; Responsible Gaming &gt; Self
              Exclusion and select the duration you wish to set
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              You will be immediately logged out of your account after
              initiating a self-exclusion, and you will be unable to login for
              the duration of the exclusion. Any balance you have at the time of
              the self-exclusion will be processed back as soon as you request
              it via any of our contact channels, and you will be removed from
              all marketing communications.
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              Once the self-exclusion term has ended, your account will remain
              excluded, and should you wish to reopen your account you must
              contact our Customer Service in writing for reinstatement
              consideration.
            </Text>
            <Text variant={textVariant} color="darkSurfacePrimary">
              We strongly recommend, should you be considering a self-exclusion
              on your Betty account, to extend this to accounts you hold with
              other gambling operators. Please also see the support agencies
              listed in the section above who will be able to offer further
              assistance.
            </Text>

            <ul className={styles.goldBullets}>
              <li>
                <Text variant={textVariant} gradient="darkSurfaceAccent">
                  Regular Account Closure
                </Text>
              </li>
            </ul>

            <Text variant={textVariant} color="darkSurfacePrimary">
              If you would like to close your account, go to Account &gt; My
              Profile &gt; Close Account. You will be able to reverse that and
              reopen your account by contacting our customer service team.
            </Text>
          </div>
        </article>

        <article className={styles.article}>
          <Text variant={mainHeadingVariant} gradient="darkSurfaceAccent">
            Betty Support
          </Text>

          <div className={styles.rowArticle}>
            <div className={styles.child}>
              <Text variant={textVariant} color="darkSurfacePrimary">
                If you find that the current limitation settings are not enough
                or if you’d like additional guidance, please contact Betty
                Support{' '}
                <a
                  href="/contact"
                  target="_blank"
                  className={styles.lightLinks}
                >
                  here
                </a>
                . Our staff is fully trained to assist you if you find it
                challenging to play responsibly.
              </Text>
            </div>
            <div className={styles.child}>
              <Image
                name="misc.rgArticle5"
                contentFit="contain"
                width="100%"
                aspectRatio="16/9"
              />
            </div>
          </div>

          <div className={styles.subGuidelinesWrap}>
            <Text
              variant={secondaryHeadingVariant}
              gradient="darkSurfaceAccent"
            >
              Common Myths about Gambling
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              There are a few common misconceptions about gaming that are not
              true and can lead to detrimental losses if we do not game
              responsibly:
            </Text>

            <Text variant={textVariant} gradient="darkSurfaceAccent">
              “If I keep playing, my luck will change, and I will win back the
              money I&apos;ve lost.”
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              The outcome of each bet is independent of the previous one. Your
              odds of winning or losing do not increase or decrease over time.
              However, over time, the more you risk, the more you can lose.
            </Text>

            <Text variant={textVariant} gradient="darkSurfaceAccent">
              “I have a special strategy that helps me win e.g. I will win if I
              stop the slot at a certain time because it worked for me before.”
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              The outcome of slot machines is completely random. You cannot
              influence it, no matter what you do. Pressing the stop button may
              help you learn the game&apos;s outcome faster, but it won&apos;t
              influence the outcome in any way.
            </Text>

            <Text variant={textVariant} gradient="darkSurfaceAccent">
              “Today is my lucky day, so I know I will win.”
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              Wishing, hoping, or praying to win money will not affect the
              outcome of a game based on pure chance.
            </Text>

            <Text variant={textVariant} gradient="darkSurfaceAccent">
              “If I play multiple slot machines, I have a higher chance at
              winning.”
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              You may win more often by playing more than one slot machine,
              however, you will also spend more. Over time, the more you gamble,
              the more you could lose.
            </Text>

            <Text variant={textVariant} gradient="darkSurfaceAccent">
              “Gambling can be a good source of income.”
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              iGaming is a recreational activity and is intended to provide
              entertainment value.
            </Text>

            <Text variant={textVariant} gradient="darkSurfaceAccent">
              “I almost won, I must be due for a win soon.”
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              Almost winning does not mean a real win is around the corner.
              Remember, any past outcomes do not influence future outcomes from
              games.
            </Text>
          </div>

          <Text variant={secondaryHeadingVariant} gradient="darkSurfaceAccent">
            How games work
          </Text>

          <div className={styles.subGuidelinesWrap}>
            <Text variant={textVariant} color="darkSurfacePrimary">
              Remember that slots are a game of pure chance. Slots use a random
              number generator (RNG), and every spin/roll is completely random
              and unpredictable.The RNG is the heart of these games, as it
              generates the combinations that determine whether you win or lose,
              and it&apos;s closely tied to the concept of RTP%.
            </Text>
            <Text variant={textVariant} color="darkSurfacePrimary">
              RTP% stands for “Return to Player percentage”. This is a term used
              in the gambling industry, particularly in online casinos and
              gambling games, to indicate the percentage of total wagered money
              that a specific game will pay back to its players over a period of
              time.
            </Text>
            <Text variant={textVariant} color="darkSurfacePrimary">
              To put it in simpler terms, let&apos;s say a slot machine has an
              RTP of 96%. This means that, theoretically, for every $100 that
              players put into the machine, it would pay back $96. The remaining
              $4 would be kept by the casino as profit.
            </Text>
            <Text variant={textVariant} color="darkSurfacePrimary">
              However, it&apos;s important to understand that RTP is calculated
              over a long period of time and a large number of games played. It
              does not mean that every time you wager $100, you will get $96
              back. Some games you might win more, and some games you might win
              less or even lose. The RTP gives you a general idea of how much
              the game pays out compared to how much it takes in.
            </Text>

            <Text
              variant={textVariant}
              color="darkSurfacePrimary"
              display="flex"
            >
              An advantage of knowing about RTP is that it can help you manage
              your spend better. By knowing how much money you can expect to
              lose on average, you can budget your funds accordingly and avoid
              overspending.
            </Text>

            <Text
              variant={secondaryHeadingVariant}
              gradient="darkSurfaceAccent"
              display="flex"
            >
              What you can do to limit gambling ads on Facebook
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              It is not possible to remove ads on the platform or from apps,
              however, you are able to hide them by selecting the down arrow
              menu or the “x” in the top right-hand corner of the sponsored
              post.
            </Text>

            <Text variant={textVariant} color="darkSurfacePrimary">
              In addition, you can{' '}
              <a
                href="https://www.facebook.com/help/247395082112892/?ref=u2u"
                target="_blank"
                className={styles.lightLinks}
                rel="noreferrer"
              >
                adjust your ads preferences
              </a>{' '}
              to see ads that are more relevant to you. To find more information
              regarding &rdquo;can I block ads&rdquo; on Facebook and &rdquo;how
              can I adjust my ad preferences&rdquo;, please view{' '}
              <a
                href="https://www.facebook.com/help/146952742043748/?ref=u2u"
                target="_blank"
                className={styles.lightLinks}
                rel="noreferrer"
              >
                this article
              </a>{' '}
              from their Help Center. Your settings can be found{' '}
              <a
                href="https://www.facebook.com/ads/preferences/?entry_product=ad_settings_screen"
                target="_blank"
                className={styles.lightLinks}
                rel="noreferrer"
              >
                here
              </a>
            </Text>
          </div>
        </article>
      </div>
      </main>
     </Page>
  );
}
export default ResponsibleGambling;
